import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
  handleCashifyBlobResp,
} from "./utils";
import type {
  WrappedCashifyFunction,
  PaginatedResponse,
  GenericResponse,
  WrappedCashifyPageableQuery,
  CommonResponse,
} from "./utils";

import type {
  ApplicationSearchCriteria,
  ApplicationDTO,
  ApplicationDetail,
  ApplicationJournalDTO,
  ListApplicationJournalRequestCriteria,
  CreateApplicationReviewRequest,
  ApplicationExportCriteria,
  ValidationError,
  ApplicationCreateRequest,
  ApplicationCreateResult,
} from "./application-types";
import { StandardObject } from "utils";
import { ApplicationAgent } from "./application-group-service";

export interface ApplicationService {
  listApplication: ReturnType<typeof listApplication>;
  createApplication: ReturnType<typeof createApplication>;
  submitApplication: ReturnType<typeof submitApplication>;
  exportApplication: ReturnType<typeof exportApplication>;
  getApplicationDetail: ReturnType<typeof getApplicationDetail>;
  patchApplicationDetail: ReturnType<typeof patchApplicationDetail>;
  listApplicationJournal: ReturnType<typeof listApplicationJournal>;
  createApplicationReview: ReturnType<typeof createApplicationReview>;
  assignApplicationAgent: ReturnType<typeof assignApplicationAgent>;
  listAccessibleAgent: ReturnType<typeof listAccessibleAgent>;
}

export const listApplication: WrappedCashifyPageableQuery<
  ApplicationSearchCriteria,
  ApplicationDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/application`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const createApplication: WrappedCashifyFunction<
  { body: ApplicationCreateRequest; submitNow?: boolean },
  GenericResponse<ApplicationCreateResult>
> = ({ urlPrefix, options }) => {
  return async ({ body, submitNow }) => {
    const urlPath = `${urlPrefix}/api/v1/application`;
    let url;
    if (!!submitNow) {
      url = getURLWithQueryParams(urlPath, { submitNow });
    } else {
      url = urlPath;
    }
    const postOpts = {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const submitApplication: WrappedCashifyFunction<
  { applicationId: string },
  GenericResponse<{ validationErrors: ValidationError[] }>
> = ({ urlPrefix, options }) => {
  return async ({ applicationId }) => {
    let url = `${urlPrefix}/api/v1/application/${applicationId}`;
    const postOpts = {
      method: "POST",
      headers: {
        ...getDefaultHeaders(),
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getApplicationDetail: WrappedCashifyFunction<
  { applicationId: string },
  GenericResponse<ApplicationDetail>
> = ({ urlPrefix, options }) => {
  return async ({ applicationId }) => {
    let url = `${urlPrefix}/api/v1/application/${applicationId}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listApplicationJournal: WrappedCashifyFunction<
  ListApplicationJournalRequestCriteria,
  PaginatedResponse<ApplicationJournalDTO>
> = ({ urlPrefix, options }) => {
  return async ({ applicationId, ...queryParams }) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/application/${applicationId}/journal`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

const getUrlPathFromType = (type: string) => {
  switch (type) {
    case "COMMENT_APPLICATION":
      return "comment";
    case "CANCEL_APPLICATION":
      return "cancel";
    case "PROCESS_APPLICATION":
      return "process";
    case "REJECT_APPLICATION":
      return "reject";
    case "RETURN_APPLICATION":
      return "return";
    default:
      throw new Error("Invalid review type");
  }
};

export const createApplicationReview: WrappedCashifyFunction<
  CreateApplicationReviewRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ applicationId, type, comments, reviewResult }) => {
    let body;
    switch (type) {
      case "COMMENT_APPLICATION":
      case "PROCESS_APPLICATION":
      case "REJECT_APPLICATION":
        body = {
          comments,
        };
        break;
      case "RETURN_APPLICATION":
        body = {
          comments,
          newStatus: "RETURNED",
          reviewResult,
        };
        break;
      default:
        body = {};
    }
    const subpath = getUrlPathFromType(type);
    let url = `${urlPrefix}/api/v1/application/${applicationId}/${subpath}`;
    let postOpts;
    if (type === "CANCEL_APPLICATION") {
      postOpts = {
        method: "POST",
        headers: {
          ...getDefaultHeaders(),
        },
      };
    } else {
      postOpts = {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      };
    }
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const exportApplication: WrappedCashifyFunction<
  ApplicationExportCriteria,
  { blob: Blob; name: string }
> = ({ urlPrefix, options }) => {
  return async ({ exportOption, signal, ...body }) => {
    const url = `${urlPrefix}/api/v1/application/${exportOption}`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify(body),
        signal,
        headers: {
          Accept: "application/octet-stream",
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleCashifyBlobResp(resp));
  };
};

export const patchApplicationDetail: WrappedCashifyFunction<
  { applicationId: string; formData: StandardObject },
  GenericResponse<{ validationErrors: ValidationError[] }>
> = ({ urlPrefix, options }) => {
  return async ({ applicationId, formData }) => {
    const url = `${urlPrefix}/api/v1/application/${applicationId}`;
    const opts = extendFetchOptions(
      {
        method: "PATCH",
        body: JSON.stringify(formData),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const assignApplicationAgent: WrappedCashifyFunction<
  { applicationId: string; agentId: number | null },
  GenericResponse<{ validationErrors: ValidationError[] }>
> = ({ urlPrefix, options }) => {
  return async ({ applicationId, agentId }) => {
    const url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/application/${applicationId}/agent`,
      { agentId: agentId === null ? "" : agentId },
      true,
    );
    const opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listAccessibleAgent: WrappedCashifyFunction<
  { applicationId: string },
  GenericResponse<ApplicationAgent>
> = ({ urlPrefix, options }) => {
  return async ({ applicationId, ...queryParams }) => {
    let url = `${urlPrefix}/api/v1/application/${applicationId}/accessible-agent`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
